import React, { useState } from 'react';
import { IconSparkles, IconTable } from '@tabler/icons-react';
import type { AnalyticsQuestion } from '../../../lib/campaign-analytics/normalize';
import type { DrillTarget } from '../../../lib/campaign-analytics/drill';
import type { DistributionSummary, MatrixSummary, NumericSummary, QuestionSummary, TimelineSummary } from '../../../lib/campaign-analytics/summarize';
import { captionFor } from '../../../lib/campaign-analytics/caption';
import { formatCompact, formatPercent, formatStat, PERCENT_MIN_ELIGIBLE } from '../../../lib/campaign-analytics/format';
import { questionPrompt } from '../../../lib/campaign-analytics/suggestions';
import { TONE_CLASSES, chartColor, heatFill, type ChartTone } from './chartTheme';
import { ColumnBars, type ColumnDatum } from './charts/ColumnBars';
import { DataTable, type DataTableProps } from './charts/DataTable';
import { Donut } from './charts/Donut';
import { Heatmap } from './charts/Heatmap';
import { PillBars, type PillDatum } from './charts/PillBars';
import { StackedBar } from './charts/StackedBar';
import { Skeleton } from '../../ui/Skeleton';
import { CollectionPresence } from '../../ui/CollectionPresence';

/** Props for {@link QuestionCard}. */
export interface QuestionCardProps {
  /** Omit until the response-derived summary is available. */
  summary?: QuestionSummary;
  /** The question behind the summary, for option thumbnails. */
  question?: AnalyticsQuestion;
  /** Opens the respondents behind a mark, with a short label for the filtered list. */
  onDrill?: (target: DrillTarget, label: string) => void;
  /** Asks hollie about this question. */
  onAsk?: (prompt: string, summary: QuestionSummary) => void;
  /** The card's hue family; neighbouring cards use different tones. */
  tone?: ChartTone;
}

/** Charts need at least this many eligible responses; below it cards show counts only. */
const CHART_MIN_ELIGIBLE = 2;
/** Long lists show this many bars before `+N more`. */
const PILL_LIMIT = 5;
/** Stacked bars carry up to this many category hues; more columns switch to a heatmap. */
const MAX_HUES = 6;
/** Key of the grouped "Other options" row. */
const REST_KEY = '\u0000rest';

/** A share, or `n of m` when the population is too small for percentages. */
function shareLabel(count: number, base: number, eligible: number): string {
  return eligible < PERCENT_MIN_ELIGIBLE ? `${count} of ${eligible}` : formatPercent(base ? count / base : 0);
}

/** Exact values on hover: the count and its share. */
function detailLabel(count: number, base: number): string {
  return `${count} of ${base} · ${formatPercent(base ? count / base : 0)}`;
}

/** Pills for a distribution: every option, the grouped rest last. */
function distributionPills(summary: DistributionSummary, question?: AnalyticsQuestion): PillDatum[] {
  const images = new Map(question && 'options' in question.field
    ? question.field.options.map(option => [question.field.type === 'image_choice' ? option.id : option.value, option.image?.imageUrl]) : []);
  const pills: PillDatum[] = summary.items.map(item => ({
    key: item.key,
    label: item.label,
    fill: item.share,
    valueLabel: shareLabel(item.count, summary.answered, summary.eligible),
    detail: detailLabel(item.count, summary.answered),
    image: images.get(item.key),
  }));
  if (summary.rest) {
    pills.push({ key: REST_KEY, label: 'Other options', fill: 0, valueLabel: shareLabel(summary.rest.count, summary.answered, summary.eligible), detail: detailLabel(summary.rest.count, summary.answered), summary: true });
  }
  return pills;
}

/** Columns for a number histogram, heights relative to the tallest. */
function numericColumns(summary: NumericSummary): ColumnDatum[] {
  const max = Math.max(1, ...summary.histogram.bins.map(bin => bin.count));
  return summary.histogram.bins.map((bin, index) => ({
    key: String(index),
    // Ranges sit under narrow columns, so their edges are compact (1K-2K).
    label: summary.histogram.kind === 'values' ? formatStat(bin.x0) : `${formatCompact(bin.x0)}-${formatCompact(bin.x1)}`,
    fill: bin.count / max,
    valueLabel: shareLabel(bin.count, summary.n, summary.eligible),
    detail: detailLabel(bin.count, summary.n),
  }));
}

/** Period label for a timeline bin, in UTC so it matches the stored date. */
function periodLabel(start: string, granularity: TimelineSummary['granularity']): string {
  const date = new Date(`${start}T00:00:00Z`);
  return granularity === 'month'
    ? date.toLocaleDateString('en', { month: 'short', year: 'numeric', timeZone: 'UTC' })
    : date.toLocaleDateString('en', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Columns from labelled counts, heights relative to the tallest. */
function countColumns(entries: Array<{ key: string; label: string; count: number }>, base: number, eligible: number): ColumnDatum[] {
  const max = Math.max(1, ...entries.map(entry => entry.count));
  return entries.map(entry => ({ key: entry.key, label: entry.label, fill: entry.count / max, valueLabel: shareLabel(entry.count, base, eligible), detail: detailLabel(entry.count, base) }));
}

/** The tables behind a card: the same values as its chart, as text. */
function tablesFor(summary: QuestionSummary): DataTableProps[] {
  switch (summary.kind) {
    case 'distribution': {
      const rows = summary.items.map(item => ({ key: item.key, label: item.label, cells: [item.count, formatPercent(item.share)] }));
      if (summary.rest) rows.push({ key: REST_KEY, label: 'Other options', cells: [summary.rest.count, formatPercent(summary.answered ? summary.rest.count / summary.answered : 0)] });
      return [{ rowHeader: 'Option', columns: ['Count', '%'], rows }];
    }
    case 'numeric': {
      const stat = (label: string, value: string) => ({ key: label, label, cells: [value] });
      return [
        {
          rowHeader: 'Statistic', columns: ['Value'], rows: [
            stat('n', String(summary.n)), stat('Mean', formatStat(summary.mean)), stat('Median', formatStat(summary.median)),
            stat('Min', formatStat(summary.min)), stat('Max', formatStat(summary.max)), stat('SD', formatStat(summary.stdDev)),
            stat('IQR', summary.q1 === null ? '' : `${formatStat(summary.q1)} to ${formatStat(summary.q3)}`),
          ],
        },
        { rowHeader: 'Value', columns: ['Count'], rows: numericColumns(summary).map(column => ({ key: column.key, label: column.label, cells: [summary.histogram.bins[Number(column.key)].count] })) },
      ];
    }
    case 'nps': {
      const groups: Array<[string, number]> = [['Detractors', summary.detractors], ['Passives', summary.passives], ['Promoters', summary.promoters]];
      return [{ rowHeader: 'Group', columns: ['Count', '%'], rows: groups.map(([label, count]) => ({ key: label, label, cells: [count, formatPercent(summary.answered ? count / summary.answered : 0)] })) }];
    }
    case 'ranking':
      return [{ rowHeader: 'Option', columns: ['Avg rank', 'First'], rows: summary.items.map(item => ({ key: item.key, label: item.label, cells: [formatStat(item.averageRank), item.firstPlace] })) }];
    case 'matrix':
      return [{ rowHeader: 'Row', columns: summary.columns.map(column => column.label), rows: summary.rows.map(row => ({ key: row.key, label: row.label, cells: row.cells.map(cell => `${cell.count} (${formatPercent(cell.share)})`) })) }];
    case 'timeline':
      return [{ rowHeader: 'Period', columns: ['Count'], rows: summary.bins.map(bin => ({ key: bin.start, label: periodLabel(bin.start, summary.granularity), cells: [bin.count] })) }];
    default:
      return [];
  }
}

/** Matrix as rounded stacked rows (up to 6 columns) or a heatmap. */
function MatrixChart({ summary }: { summary: MatrixSummary }) {
  if (summary.columns.length <= MAX_HUES) {
    return (
      <StackedBar rows={summary.rows.map(row => ({
        key: row.key,
        label: row.label,
        segments: row.cells.map((cell, index) => ({ key: cell.key, label: cell.label, count: cell.count, color: chartColor.categorical[index] })),
      }))} />
    );
  }
  const max = Math.max(1, ...summary.rows.flatMap(row => row.cells.map(cell => cell.count)));
  return (
    <Heatmap
      rows={summary.rows}
      columns={summary.columns}
      cells={summary.rows.map(row => row.cells.map(cell => ({ key: cell.key, text: String(cell.count), fill: heatFill(cell.count / max), label: `${row.label} · ${cell.label}`, detail: `${cell.count} · ${formatPercent(cell.share)}` })))}
      scale={{ low: '0', high: String(max), lowFill: heatFill(0), highFill: heatFill(1) }}
    />
  );
}

/** The chart for a summary. */
function Chart({ summary, question, onDrill }: Pick<QuestionCardProps, 'question' | 'onDrill'> & { summary: QuestionSummary }) {
  switch (summary.kind) {
    case 'distribution': {
      const drill = onDrill ? (key: string, label: string) => onDrill({ type: 'category', fieldId: summary.fieldId, key }, `${summary.label} · ${label}`) : undefined;
      if (summary.view === 'donut') {
        return <Donut data={summary.items.map(item => ({ key: item.key, label: item.label, count: item.count, valueLabel: shareLabel(item.count, summary.answered, summary.eligible) }))}
          onSelect={drill ? datum => drill(datum.key, datum.label) : undefined} />;
      }
      if (summary.medianKey !== undefined) {
        // Faces ratings read as a scale: columns in face order.
        return <ColumnBars data={countColumns(summary.items, summary.answered, summary.eligible)} onSelect={drill ? datum => drill(datum.key, datum.label) : undefined} />;
      }
      return (
        <div className="flex flex-col gap-3">
          <PillBars data={distributionPills(summary, question)} limit={summary.order === 'count' ? PILL_LIMIT : undefined} hiddenExtra={summary.rest?.options ?? 0}
            onSelect={drill ? datum => drill(datum.key, datum.label) : undefined} />
          {summary.otherTexts.length > 0 && (
            <ul className="max-h-24 overflow-y-auto rounded-xl bg-surface-hover/50 px-3 py-2 text-xs text-muted-foreground">
              {summary.otherTexts.map((text, index) => <li key={index} className="truncate" data-tip={text} data-tip-truncated>{text}</li>)}
            </ul>
          )}
        </div>
      );
    }
    case 'numeric': {
      const last = summary.histogram.bins.length - 1;
      const ends = summary.scale ? {
        low: summary.scale.direction === 'descending' ? summary.scale.highLabel : summary.scale.lowLabel,
        high: summary.scale.direction === 'descending' ? summary.scale.lowLabel : summary.scale.highLabel,
      } : undefined;
      return (
        <ColumnBars data={numericColumns(summary)} ends={ends} onSelect={onDrill ? (datum, index) => {
          const bin = summary.histogram.bins[index];
          onDrill({ type: 'range', fieldId: summary.fieldId, x0: bin.x0, x1: bin.x1, closed: summary.histogram.kind === 'values' || index === last }, `${summary.label} · ${datum.label}`);
        } : undefined} />
      );
    }
    case 'nps':
      return <ColumnBars data={countColumns(summary.distribution.map(entry => ({ key: String(entry.value), label: String(entry.value), count: entry.count })), summary.answered, summary.eligible)} />;
    case 'ranking':
      // Each bar is the share who ranked the option first; order is by average rank.
      return <PillBars data={summary.items.map(item => ({
        key: item.key, label: item.label, fill: summary.answered ? item.firstPlace / summary.answered : 0,
        valueLabel: `${shareLabel(item.firstPlace, summary.answered, summary.eligible)} first`, detail: `Average rank ${formatStat(item.averageRank)}`,
      }))} />;
    case 'matrix':
      return <MatrixChart summary={summary} />;
    case 'timeline':
      return <ColumnBars data={countColumns(summary.bins.map(bin => ({ key: bin.start, label: periodLabel(bin.start, summary.granularity), count: bin.count })), summary.answered, summary.eligible)} />;
    default:
      return null;
  }
}

/**
 * One question as a calm card: the question, one plain caption, a friendly
 * chart and how many answered, with a table view and an Ask hollie link. No
 * helper copy, no lead number.
 */
export const QuestionCard: React.FC<QuestionCardProps> = ({ summary, question, onDrill, onAsk, tone = 'volt' }) => {
  const [tableFor, setTableFor] = useState<string | null>(null);
  const table = Boolean(summary && tableFor === summary.fieldId);
  const chartable = !summary || summary.eligible >= CHART_MIN_ELIGIBLE;
  const caption = summary && captionFor(summary);
  const answered = summary && `${summary.answered} of ${summary.eligible} answered${summary.excluded > 0 ? ` · ${summary.excluded} excluded` : ''}`;
  return (
    <article aria-busy={!summary} className={`${TONE_CLASSES[tone]} flex min-w-0 flex-col gap-5 rounded-[20px] p-6`}>
      <CollectionPresence contentKey={summary ? 'ready' : 'loading'}><div className="flex min-w-0 flex-col gap-5">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-medium text-foreground">{summary?.label ?? question?.label ?? <Skeleton width="70%" inline />}</h3>
          {(!summary || caption) && <p className="mt-1 text-sm text-muted-foreground">{caption ?? <Skeleton width="85%" inline />}</p>}
        </div>
        {chartable && (
          <button type="button" aria-label="Table" aria-pressed={table} disabled={!summary} onClick={() => summary && setTableFor(table ? null : summary.fieldId)}
            className={`shrink-0 rounded-full p-2 hover:bg-[var(--chart-track)] focus-visible:outline-2 focus-visible:outline-ring ${table ? 'text-foreground' : 'text-subtle-foreground'}`}>
            <IconTable aria-hidden="true" className="h-4 w-4" />
          </button>
        )}
      </header>
      {!summary ? <div className="text-sm"><Skeleton count={3} /></div> : chartable && (table
        ? <div className="flex flex-col gap-3">{tablesFor(summary).map(props => <DataTable key={props.rowHeader} {...props} />)}</div>
        : <Chart summary={summary} question={question} onDrill={onDrill} />)}
      <footer className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
        <span className="tabular-nums">{answered ?? <Skeleton width="12ch" inline />}</span>
        {onAsk && (
          <button type="button" disabled={!summary} onClick={() => summary && onAsk(questionPrompt(summary.label), summary)}
            className="flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-accent-soft hover:bg-[var(--chart-track)] focus-visible:outline-2 focus-visible:outline-ring">
            <IconSparkles aria-hidden="true" className="h-3.5 w-3.5" />
            Ask hollie
          </button>
        )}
      </footer>
      </div></CollectionPresence>
    </article>
  );
};

/** Props for {@link QuestionLine}. */
export interface QuestionLineProps {
  summary: QuestionSummary;
}

/** A question with no picture as one line: everyone gave the same answer, or only an answered count. */
export const QuestionLine: React.FC<QuestionLineProps> = ({ summary }) => {
  const value = summary.kind === 'distribution' && summary.items.length
    ? `${summary.answered} of ${summary.eligible} · ${summary.items.map(item => item.label).join(', ')}`
    : `${summary.answered} of ${summary.eligible}`;
  return (
    <div className="flex min-w-0 flex-col gap-0.5 px-2 py-3 text-sm">
      <span className="text-foreground">{summary.label}</span>
      <span className="min-w-0 truncate tabular-nums text-muted-foreground" data-tip={value} data-tip-truncated>{value}</span>
    </div>
  );
};
